import { useForm, router } from '@inertiajs/react'
import type { FormEvent, ReactNode } from 'react'
import Layout from '../../layouts/Layout'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Card from '../../components/ui/Card'

interface User {
  id: string
  username: string
  fullName: string
  phoneNumber: string
  location: string
  timeZone: string
  email: string
}

interface Props {
  user: User
  errors?: Record<string, string[]>
}

export default function Edit({ user, errors: serverErrors }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    user: {
      full_name: user.fullName || '',
      username: user.username || '',
      phone_number: user.phoneNumber || '',
      location: user.location || '',
      email: user.email || '',
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    patch(`/users/${user.username}`)
  }

  const handleDelete = () => {
    if (
      window.confirm(
        `Are you absolutely sure you want to delete your account @${user.username}? This action cannot be undone. All your posts and data will be permanently deleted.`
      )
    ) {
      router.delete(`/users/${user.username}`)
    }
  }

  const getError = (field: string) => {
    return errors[field as keyof typeof errors] || serverErrors?.[field]?.[0]
  }

  return (
    <Layout title="Settings">
      <div className="container mx-auto px-4 py-8 max-w-lg">
        <h1 className="text-3xl font-bold mb-8">Settings</h1>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Full Name"
              value={data.user.full_name}
              onChange={(e) => setData('user', { ...data.user, full_name: e.target.value })}
              placeholder="Your Name"
              required
              error={getError('full_name')}
            />

            <div>
              <Input
                label="Username"
                value={data.user.username}
                onChange={(e) => setData('user', { ...data.user, username: e.target.value })}
                required
                error={getError('username')}
              />
              <p className="mt-1 text-sm text-gray-500">
                Your{' '}
                <a
                  href={`https://three.pm/${user.username}`}
                  className="underline hover:text-black"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  public profile
                </a>{' '}
                and{' '}
                <a
                  href={`https://three.pm/${user.username}/feed.xml`}
                  className="underline hover:text-black"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  RSS feed
                </a>
                .
              </p>
            </div>

            <Input
              label="Phone Number or Apple ID"
              value={data.user.phone_number}
              onChange={(e) => setData('user', { ...data.user, phone_number: e.target.value })}
              placeholder="(555) 867-5309 or email@icloud.com"
              required
              error={getError('phone_number')}
            />

            <Input
              label="Location"
              value={data.user.location}
              onChange={(e) => setData('user', { ...data.user, location: e.target.value })}
              placeholder="Place, Earth"
              required
              error={getError('location')}
            />

            <div>
              <Input
                label="Time Zone"
                value={user.timeZone || ''}
                disabled
                className="bg-gray-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Change your location to update your time zone.
              </p>
            </div>

            <Input
              label="Email"
              type="email"
              value={data.user.email}
              onChange={(e) => setData('user', { ...data.user, email: e.target.value })}
              placeholder="your@email.com"
              required
              error={getError('email')}
            />

            <Button type="submit" disabled={processing} className="w-full">
              {processing ? 'Saving...' : 'Save'}
            </Button>
          </form>
        </Card>

        {/* Danger Zone */}
        <Card className="mt-8 border-red-200">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Danger Zone
          </h3>
          <p className="text-gray-600 mb-4">
            Once you delete your account, there is no going back. Please be
            certain.
          </p>
          <Button
            variant="secondary"
            onClick={handleDelete}
            className="border-red-500 text-red-500 hover:bg-red-50"
          >
            Delete Account
          </Button>
        </Card>
      </div>
    </Layout>
  )
}

Edit.layout = (page: ReactNode) => page
